import { Button, Dialog, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { appStore } from 'stores/appStore';
import { AlarmZoneMode, GateType } from 'shared';
import {
	isTTLockLocation,
	useChangeZoneAlarmState,
	useDisarmLocker,
	useOpenItem,
	useOpenTTLock,
} from '../alerts.logic';
import { StyledButtonsWrapper, StyledDialogContent } from '../StyledComponents';
import { DualTitleDialog } from './DualTitleDialog';
import { observer } from 'mobx-react-lite';

/**
 * Which button opened the dialog. It cannot be inferred from the item: a box with an alarm can be both
 * opened and armed, and reading the alarm off the item turned every open into an arming.
 */
export type ItemDialogAction = 'open' | 'alarm';

interface IOpenItemDialogProps {
	entityId: string;
	open: boolean;
	action: ItemDialogAction;
	isLocker: boolean;
	gateType: GateType;
	userFriendlyId: string;
	typeLabel: string;
	hasAlarm: boolean;
	isArmed?: boolean;
	isMonitored?: boolean;
	alarmZoneMode?: AlarmZoneMode;
	// Set on a box whose alarm zone covers only that box - its alarm action is that zone's action.
	linkedAlarmZoneId?: string;
	linkedAlarmZoneMode?: AlarmZoneMode;
	// True on a box covered by an ExtendDisarm zone: the alarm action is a disarm, never an arm.
	hasExtendDisarmZone?: boolean;
	onClose: () => void;
}

export const OpenItemDialog = observer(
	({
		open,
		action,
		isLocker,
		gateType,
		userFriendlyId,
		typeLabel,
		onClose,
		entityId,
		hasAlarm,
		isArmed,
		isMonitored = true,
		alarmZoneMode,
		linkedAlarmZoneId,
		linkedAlarmZoneMode,
		hasExtendDisarmZone,
	}: IOpenItemDialogProps) => {
		const { t } = useTranslation();

		const isAlarmAction = action === 'alarm';

		// A zone action comes either from a zone row or from a box whose zone covers only that box - in
		// the second case the box's alarm simply is the zone, so it acts on the zone directly.
		const effectiveZoneMode = alarmZoneMode ?? linkedAlarmZoneMode;
		const zoneId = alarmZoneMode != null ? entityId : linkedAlarmZoneId;
		const isZoneAction = isAlarmAction && effectiveZoneMode != null && !!zoneId;
		const isMode1Zone = effectiveZoneMode === AlarmZoneMode.ExtendDisarm;

		// Mode 1 zones can only be disarmed/extended; everything else toggles the current armed state.
		const newZoneAlarmState = isMode1Zone ? false : !isArmed;

		// A box covered by an ExtendDisarm zone: the alarm action is always a disarm, so it must not follow
		// the box's own armed state - that would turn the button into an arming whenever the box reads as
		// disarmed. The command fans out onto every zone covering the box.
		const isExtendDisarmBox = !isZoneAction && hasExtendDisarmZone === true;
		const hasAlarmAction = hasAlarm || isExtendDisarmBox;
		const disarms = isExtendDisarmBox || !!isArmed;

		const { openItem } = useOpenItem(isLocker, gateType, entityId, isMonitored);
		const { openTTLock } = useOpenTTLock(isLocker, entityId);
		const { changeAlarmState } = useDisarmLocker(entityId, !disarms);
		const { changeAlarmState: changeZoneAlarmState } = useChangeZoneAlarmState(zoneId ?? entityId, newZoneAlarmState);

		const handleOnClose = (_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
			if (!reason) {
				onClose();
			}
		};

		const handleOpenItem = async () => {
			const itemClosed = await (isTTLockLocation() ? openTTLock() : openItem());
			if (itemClosed) {
				onClose();
			}
		};

		const handleChangeAlarmState = async () => {
			const itemClosed = await changeAlarmState();
			if (itemClosed) {
				onClose();
			}
		};

		const handleChangeZoneAlarmState = async () => {
			const itemClosed = await changeZoneAlarmState();
			if (itemClosed) {
				onClose();
			}
		};

		const dialogTitle = () => {
			if (!isAlarmAction) return t('common.open');

			if (isZoneAction) {
				// An ExtendDisarm zone only ever gets disarmed, so it does not follow the armed state.
				if (isMode1Zone) return t('common.disarmZone');
				return isArmed ? t('common.disarmZone') : t('common.armZone');
			}

			if (gateType === GateType.Alarm) return t('common.disarmAlarm');

			if (hasAlarmAction) return disarms ? t('common.disarmAlarm') : t('common.armAlarm');

			return `${t('common.open')}`;
		};

		const secondaryDialogTitle = () => `${typeLabel}: ${userFriendlyId}`;

		const dialogContent = () => {
			if (!isAlarmAction) {
				return `${t('pages.locations.openWarning')} ${isLocker ? t('pages.locations.box') : t('pages.locations.gate')}?`;
			}

			if (isZoneAction) {
				if (isMode1Zone) return `${t('pages.locations.disarmZoneWarning')}: `;
				return `${isArmed ? t('pages.locations.disarmZoneWarning') : t('pages.locations.armZoneWarning')}: `;
			}

			if (gateType === GateType.Alarm) return `${t('pages.locations.disarmWarning')}: `;

			if (hasAlarmAction) return `${disarms ? t('pages.locations.disarmWarning') : t('pages.locations.armWarning')}: `;

			return `${t('pages.locations.openWarning')} ${isLocker ? t('pages.locations.box') : t('pages.locations.gate')}?`;
		};

		const actionButtonText = () => {
			if (!isAlarmAction) return t('common.open');

			if (isZoneAction) {
				if (isMode1Zone) return t('common.disarm');
				return isArmed ? t('common.disarm') : t('common.arm');
			}

			if (hasAlarmAction) return disarms ? t('common.disarm') : t('common.arm');

			return gateType === GateType.Alarm ? t('common.disarm') : t('common.open');
		};

		const handleConfirm = () => {
			if (!isAlarmAction) return handleOpenItem();
			if (isZoneAction) return handleChangeZoneAlarmState();
			if (hasAlarmAction) return handleChangeAlarmState();
			return handleOpenItem();
		};

		return (
			<Dialog open={open} onClose={handleOnClose}>
				<DualTitleDialog dialogTitle={dialogTitle()} dialogTitleSecondary={secondaryDialogTitle()} />
				<StyledDialogContent>{dialogContent()}</StyledDialogContent>
				<DialogActions>
					<StyledButtonsWrapper>
						<Button variant='outlined' onClick={onClose}>
							{t('common.cancel')}
						</Button>
						<Button variant='contained' onClick={handleConfirm} disabled={appStore.isLoading}>
							{actionButtonText()}
						</Button>
					</StyledButtonsWrapper>
				</DialogActions>
			</Dialog>
		);
	}
);
